import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Switch,
  useToast,
  Heading,
  Divider,
  Text,
  useColorModeValue,
  Badge,
  Spinner,
  Link,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiCreateNotification,
  apiEditNotification,
  apiGetAllNotificationsAdmin,
  apiGetBranches,
  apiCalculateRecipients,
  apiGetNotificationMetadata,
} from 'api';
import { RichTextEditor } from 'components/notifications/RichTextEditor';
import DateTimeField from 'components/fields/DateTimeField';
import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';
import MultiSelect from 'components/fields/MultiSelect';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import type {
  Notification,
  NotificationAttachment,
  APIPaginatedResponse,
} from 'types/api.types';
import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';

interface MetadataOption {
  id: number;
  name: string;
  code?: string; // Optional - only for provinces
}

export const NotificationsManagementView = () => {
  const isNotificationsEnabled = useFeatureFlag('enable-big-communications');
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const existingAttachmentBg = useColorModeValue('gray.50', 'gray.700');

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    requires_acknowledgement: false,
    scheduled_for: null as Date | null,
    target_branches: [] as number[],
    target_provinces: [] as number[],
    target_roles: [] as number[],
    target_corporate_leadership: false,
  });

  const [estimatedCount, setEstimatedCount] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [editingNotificationId, setEditingNotificationId] = useState<
    number | null
  >(null);
  const [existingAttachments, setExistingAttachments] = useState<
    NotificationAttachment[]
  >([]);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);

  // Fetch metadata (roles and provinces)
  const { data: metadataData } = useQuery<{
    provinces: MetadataOption[];
    roles: MetadataOption[];
  }>({
    queryKey: ['notification-metadata'],
    queryFn: apiGetNotificationMetadata,
    enabled: isNotificationsEnabled,
  });

  const PROVINCES: MetadataOption[] = metadataData?.provinces || [];
  const ROLES: MetadataOption[] = metadataData?.roles || [];

  // Create combined roles array with corporate leadership
  const COMBINED_ROLES: MetadataOption[] = [
    ...ROLES,
    { id: -1, name: 'Corporate Leadership' },
  ];

  // Get combined selected role IDs (regular roles + corporate leadership if selected)
  const getCombinedSelectedRoleIds = (): number[] => {
    const roleIds = [...formData.target_roles];
    if (formData.target_corporate_leadership) {
      roleIds.push(-1);
    }
    return roleIds;
  };

  // Handle combined role selection
  const handleCombinedRoleChange = (selectedIds: number[]) => {
    const regularRoleIds = selectedIds.filter((id) => id !== -1);
    const hasCorporateLeadership = selectedIds.includes(-1);

    setFormData((prev) => ({
      ...prev,
      target_roles: regularRoleIds,
      target_corporate_leadership: hasCorporateLeadership,
    }));
  };

  // Fetch branches
  const { data: branchesData } = useQuery({
    queryKey: ['branches', 'all'],
    queryFn: () => apiGetBranches(0, 'all'),
    enabled: isNotificationsEnabled, // Only fetch if feature is enabled
  });

  const branches = Array.isArray(branchesData)
    ? branchesData
    : branchesData?.results || [];

  // Fetch existing notifications with pagination
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'admin', page],
    queryFn: () => apiGetAllNotificationsAdmin(page + 1),
    enabled: isNotificationsEnabled, // Only fetch if feature is enabled
  }) as { data: APIPaginatedResponse<Notification>; isLoading: boolean };

  const notifications: Notification[] = data?.results || [];
  const totalCount = data?.count || 0;

  // Calculate recipients from DB in real-time
  useEffect(() => {
    const calculateCount = async () => {
      if (
        formData.target_branches.length === 0 &&
        formData.target_provinces.length === 0 &&
        formData.target_roles.length === 0 &&
        !formData.target_corporate_leadership
      ) {
        setEstimatedCount(0);
        return;
      }

      setIsCalculating(true);
      try {
        const response = await apiCalculateRecipients({
          target_branches: formData.target_branches,
          target_provinces: formData.target_provinces,
          target_roles: formData.target_roles,
          target_corporate_leadership: formData.target_corporate_leadership,
        });
        setEstimatedCount(response.count);
      } catch (error) {
        console.error('Error calculating recipients:', error);
        setEstimatedCount(0);
      } finally {
        setIsCalculating(false);
      }
    };

    // Debounce the calculation
    const timer = setTimeout(() => {
      calculateCount();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    formData.target_branches,
    formData.target_provinces,
    formData.target_roles,
    formData.target_corporate_leadership,
  ]);

  const createMutation = useMutation({
    mutationFn: ({ data, files }: { data: any; files: File[] }) =>
      editingNotificationId
        ? apiEditNotification(editingNotificationId, data, files)
        : apiCreateNotification(data, files),
    onSuccess: (data) => {
      toast({
        title: editingNotificationId
          ? 'Notification updated'
          : 'Notification created',
        description: editingNotificationId
          ? 'Notification has been updated successfully'
          : `Sent to ${data.recipients_count} users`,
        status: 'success',
        duration: 5000,
      });
      // Reset form
      setFormData({
        title: '',
        summary: '',
        content: '',
        requires_acknowledgement: false,
        scheduled_for: null,
        target_branches: [],
        target_provinces: [],
        target_roles: [],
        target_corporate_leadership: false,
      });
      setUploadedFiles([]);
      setExistingAttachments([]);
      setEditingNotificationId(null);
      setShowForm(false);
      // Refetch list
      queryClient.invalidateQueries({ queryKey: ['notifications', 'admin'] });
    },
    onError: (error: any) => {
      toast({
        title: editingNotificationId
          ? 'Error updating notification'
          : 'Error creating notification',
        description: error?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.title || !formData.summary || !formData.content) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in title, summary, and content',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (
      formData.target_branches.length === 0 &&
      formData.target_provinces.length === 0 &&
      formData.target_roles.length === 0 &&
      !formData.target_corporate_leadership
    ) {
      toast({
        title: 'No recipients selected',
        description:
          'Please select at least one branch, province, role, or Corporate Leadership',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Show confirmation screen
    onConfirmOpen();
  };

  const handleConfirmSend = () => {
    // Format the date for backend
    const payload = {
      ...formData,
      scheduled_for: formData.scheduled_for
        ? formData.scheduled_for.toISOString()
        : null,
    };

    createMutation.mutate({ data: payload, files: uploadedFiles });
    onConfirmClose();
  };

  // Helper function to ensure value is an array
  const ensureArray = (value: any): number[] => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    // If it's a single value, convert to array
    return [value];
  };

  const handleEdit = (notification: Notification) => {
    setEditingNotificationId(notification.id);
    setFormData({
      title: notification.title,
      summary: notification.summary || '',
      content: notification.content,
      requires_acknowledgement: notification.requires_acknowledgement,
      scheduled_for: notification.scheduled_for
        ? new Date(notification.scheduled_for)
        : null,
      target_branches: ensureArray(notification.target_branches),
      target_provinces: ensureArray(notification.target_provinces),
      target_roles: ensureArray(notification.target_roles),
      target_corporate_leadership:
        notification.target_corporate_leadership || false,
    });

    // Set existing attachments
    setExistingAttachments(notification.attachments || []);

    // Show the form
    setShowForm(true);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingNotificationId(null);
    setFormData({
      title: '',
      summary: '',
      content: '',
      requires_acknowledgement: false,
      scheduled_for: null,
      target_branches: [],
      target_provinces: [],
      target_roles: [],
      target_corporate_leadership: false,
    });
    setUploadedFiles([]);
    setExistingAttachments([]);
  };

  const handleViewNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    onViewOpen();
  };

  // Don't render if feature flag is disabled
  if (!isNotificationsEnabled) {
    return (
      <Box p={6} textAlign="center">
        <Text>This feature is not available.</Text>
      </Box>
    );
  }

  return (
    <Box w="100%">
      {/* Create/Edit Notification Section */}
      {showForm && (
        <Card mb="20px" w="100%">
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color={textColor}>
              {editingNotificationId
                ? 'Edit Communication'
                : 'New Communication'}
            </Heading>
            <Divider />

            {/* Title */}
            <FormControl isRequired>
              <FormLabel fontWeight="semibold">Communication Title</FormLabel>
              <Input
                variant="main"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Important Policy Update"
                size="lg"
              />
            </FormControl>

            {/* Summary */}
            <FormControl isRequired>
              <FormLabel fontWeight="semibold">Brief Summary</FormLabel>
              <Textarea
                variant="main"
                value={formData.summary}
                onChange={(e) =>
                  setFormData({ ...formData, summary: e.target.value })
                }
                placeholder="A brief summary that will appear in notification lists (2-3 sentences)"
                rows={3}
              />
            </FormControl>

            {/* Rich Text Content Editor with integrated file upload */}
            <FormControl isRequired>
              <FormLabel fontWeight="semibold">Content</FormLabel>
              <RichTextEditor
                key={editingNotificationId || 'new'}
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
                onFilesChange={setUploadedFiles}
              />

              {/* Display existing attachments when editing */}
              {editingNotificationId && existingAttachments.length > 0 && (
                <Box mt={4} p={4} borderWidth="1px" borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Existing Attachments
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {existingAttachments.map((attachment) => (
                      <HStack
                        key={attachment.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        justify="space-between"
                        bg={existingAttachmentBg}
                      >
                        <HStack>
                          <DownloadIcon />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {attachment.filename}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {(attachment.file_size / 1024).toFixed(2)} KB
                            </Text>
                          </VStack>
                        </HStack>
                        <Link href={attachment.file} isExternal>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                          >
                            Download
                          </Button>
                        </Link>
                      </HStack>
                    ))}
                  </VStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Note: Uploading new files will add to existing attachments
                  </Text>
                </Box>
              )}
            </FormControl>

            {/* Recipients Section */}
            <Box>
              <Heading size="md" mb={4}>
                Recipients
              </Heading>

              <VStack align="stretch" spacing={4}>
                {/* Branch Filter */}
                <FormControl>
                  <FormLabel fontWeight="semibold">
                    Filter by Office/Branch
                  </FormLabel>
                  <MultiSelect
                    selectedIds={formData.target_branches}
                    availableItems={branches}
                    isLoading={false}
                    onChange={(selectedIds) =>
                      setFormData((prev) => ({
                        ...prev,
                        target_branches: selectedIds,
                      }))
                    }
                    label="Select branches..."
                    itemLabelSingular="branch"
                    itemLabelPlural="branches"
                    colorScheme="purple"
                  />
                </FormControl>

                {/* Province Filter */}
                <FormControl>
                  <FormLabel fontWeight="semibold">
                    Filter by Province
                  </FormLabel>
                  <MultiSelect
                    selectedIds={formData.target_provinces}
                    availableItems={PROVINCES}
                    isLoading={false}
                    onChange={(selectedIds) =>
                      setFormData((prev) => ({
                        ...prev,
                        target_provinces: selectedIds,
                      }))
                    }
                    label="Select provinces..."
                    itemLabelSingular="province"
                    itemLabelPlural="provinces"
                    colorScheme="green"
                  />
                </FormControl>

                {/* Role Filter */}
                <FormControl>
                  <FormLabel fontWeight="semibold">
                    Filter by Title/Role
                  </FormLabel>
                  <MultiSelect
                    selectedIds={getCombinedSelectedRoleIds()}
                    availableItems={COMBINED_ROLES}
                    isLoading={false}
                    onChange={handleCombinedRoleChange}
                    label="Select roles..."
                    itemLabelSingular="role"
                    itemLabelPlural="roles"
                    colorScheme="orange"
                  />
                </FormControl>

                {/* Estimated Count - Real from DB */}
                {(formData.target_branches.length > 0 ||
                  formData.target_provinces.length > 0 ||
                  formData.target_roles.length > 0 ||
                  formData.target_corporate_leadership) && (
                  <Box
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    borderWidth="2px"
                    borderColor="blue.200"
                  >
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontWeight="bold" color="blue.700" fontSize="lg">
                          Estimated Recipients:{' '}
                          {isCalculating ? '...' : estimatedCount} users
                        </Text>
                        {isCalculating && (
                          <Spinner size="sm" color="blue.500" />
                        )}
                      </HStack>
                      <Text fontSize="sm" color="blue.600" fontStyle="italic">
                        Please note that corporate leadership has been included
                        on all communications and are reflected in the estimated
                        recipient list.
                      </Text>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Scheduling */}
            <DateTimeField
              label="Schedule for later (optional)"
              date={formData.scheduled_for}
              onChange={(date: Date | null) =>
                setFormData({ ...formData, scheduled_for: date })
              }
              minDate={new Date()}
              isClearable
              htmlID="scheduled_for"
            />
            {/* <Text fontSize="sm" color="gray.600">
            Leave empty to send immediately
          </Text> */}

            {/* Requires Acknowledgement */}
            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0" fontWeight="semibold" marginLeft="0">
                Requires Acknowledgement
              </FormLabel>
              <Switch
                size="lg"
                isChecked={formData.requires_acknowledgement}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    requires_acknowledgement: e.target.checked,
                  })
                }
              />
            </FormControl>
            {/* <Text fontSize="sm" color="gray.600" mt={-4}>
            When enabled, users must acknowledge this communication before
            accessing the platform
          </Text> */}

            <Divider />

            {/* Submit and Cancel Buttons */}
            <Flex mt={10} justify="flex-end">
              <Button
                variant="outline"
                mr={3}
                onClick={() => {
                  if (editingNotificationId) {
                    handleCancelEdit();
                  }
                  setShowForm(false);
                }}
                isDisabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="brand"
                onClick={handleSubmit}
                isLoading={createMutation.isPending}
              >
                {editingNotificationId
                  ? 'Update Communication'
                  : formData.scheduled_for
                    ? 'Schedule Communication'
                    : 'Send Now'}
              </Button>
            </Flex>
          </VStack>
        </Card>
      )}

      {/* BIG Communications Section - Combined */}
      {!showForm && (
        <Card w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
          <Flex
            px="20px"
            justifyContent="space-between"
            align="center"
            mb="20px"
          >
            <Text
              color={textColor}
              fontSize="22px"
              fontWeight="700"
              lineHeight="100%"
            >
              BIG Communications
            </Text>
            <Button
              leftIcon={<AddIcon />}
              variant="brand"
              size="md"
              onClick={() => setShowForm(true)}
            >
              New Communication
            </Button>
          </Flex>

          <Table variant="simple" color="gray.500">
            <Thead>
              <Tr>
                <Th pe="10px" borderColor={borderColor} color="gray.400">
                  Title
                </Th>
                <Th pe="10px" borderColor={borderColor} color="gray.400">
                  Recipients
                </Th>
                <Th pe="10px" borderColor={borderColor} color="gray.400">
                  Created At
                </Th>
                <Th pe="10px" borderColor={borderColor} color="gray.400">
                  Status
                </Th>
                <Th pe="10px" borderColor={borderColor} color="gray.400">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Flex align="center" justify="center" py="20px">
                      <Spinner size="sm" mr="10px" /> Loading communications...
                    </Flex>
                  </Td>
                </Tr>
              )}

              {!isLoading && notifications.length === 0 && (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Text color="gray.500" py="20px">
                      No communications created yet
                    </Text>
                  </Td>
                </Tr>
              )}

              {notifications.map((notification) => (
                <Tr
                  key={notification.id}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleViewNotification(notification)}
                >
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {notification.title.length > 45
                          ? `${notification.title.substring(0, 45)}...`
                          : notification.title}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {notification.recipient_count || 0}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm">
                        {new Date(notification.created_at).toLocaleString()}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <HStack spacing={1}>
                        {notification.is_edited && (
                          <Badge colorScheme="blue" fontSize="xs">
                            Edited
                          </Badge>
                        )}
                        {notification.requires_acknowledgement && (
                          <Badge colorScheme="red" fontSize="xs">
                            Mandatory
                          </Badge>
                        )}
                        {notification.scheduled_for && (
                          <Badge colorScheme="orange" fontSize="xs">
                            Scheduled
                          </Badge>
                        )}
                      </HStack>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(notification);
                        }}
                      >
                        Edit
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          <Paginate
            page={page}
            count={totalCount}
            onSetPage={setPage}
            pageSize={20}
          />
        </Card>
      )}

      {/* View Notification Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxH="80vh" display="flex" flexDirection="column">
          <ModalHeader>
            <Text fontSize="xl" fontWeight="bold">
              {selectedNotification?.title}
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <Divider />

          <ModalBody overflow="auto" flex="1" py={6}>
            {selectedNotification && (
              <VStack align="start" spacing={4}>
                <HStack spacing={2} flexWrap="wrap">
                  {selectedNotification.is_edited && (
                    <Badge colorScheme="blue">Edited</Badge>
                  )}
                  {selectedNotification.requires_acknowledgement && (
                    <Badge colorScheme="red">Mandatory</Badge>
                  )}
                  <Badge colorScheme="gray">
                    {selectedNotification.recipient_count || 0} recipients
                  </Badge>
                  {selectedNotification.scheduled_for && (
                    <Badge colorScheme="orange">
                      Scheduled:{' '}
                      {new Date(
                        selectedNotification.scheduled_for,
                      ).toLocaleString()}
                    </Badge>
                  )}
                </HStack>

                <Divider />

                <Box
                  w="full"
                  dangerouslySetInnerHTML={{
                    __html: selectedNotification.content,
                  }}
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 'md',
                      my: 2,
                    },
                    '& a': {
                      color: 'blue.500',
                      textDecoration: 'underline',
                    },
                    '& h1, & h2, & h3': {
                      fontWeight: 'bold',
                      my: 2,
                    },
                    '& ul, & ol': {
                      pl: 6,
                    },
                  }}
                />

                {/* Attachments Section */}
                {selectedNotification.attachments &&
                  selectedNotification.attachments.length > 0 && (
                    <>
                      <Divider />
                      <Box w="full">
                        <Text fontWeight="semibold" mb={2}>
                          Attachments
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {selectedNotification.attachments.map(
                            (attachment) => (
                              <HStack
                                key={attachment.id}
                                p={3}
                                borderWidth="1px"
                                borderRadius="md"
                                justify="space-between"
                              >
                                <HStack>
                                  <DownloadIcon />
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {attachment.filename}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {(attachment.file_size / 1024).toFixed(2)}{' '}
                                      KB
                                    </Text>
                                  </VStack>
                                </HStack>
                                <Link href={attachment.file} isExternal>
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    variant="outline"
                                  >
                                    Download
                                  </Button>
                                </Link>
                              </HStack>
                            ),
                          )}
                        </VStack>
                      </Box>
                    </>
                  )}

                <Divider />

                {/* Targeted Groups in Modal - Only visible to admins and corporate leadership */}
                {(user.role === ROLE_ADMIN ||
                  selectedNotification.target_corporate_leadership) && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Target Audience:
                    </Text>
                    <VStack align="stretch" spacing={2} pl={4}>
                      {selectedNotification.target_branches &&
                        selectedNotification.target_branches.length > 0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.600"
                            >
                              Branches/Offices:
                            </Text>
                            <HStack spacing={2} flexWrap="wrap" mt={1}>
                              {branches
                                .filter((b: any) =>
                                  selectedNotification.target_branches.includes(
                                    b.id,
                                  ),
                                )
                                .map((b: any) => (
                                  <Badge key={b.id} colorScheme="purple">
                                    {b.name}
                                  </Badge>
                                ))}
                            </HStack>
                          </Box>
                        )}
                      {selectedNotification.target_provinces_display &&
                        selectedNotification.target_provinces_display.length >
                          0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.600"
                            >
                              Provinces:
                            </Text>
                            <HStack spacing={2} flexWrap="wrap" mt={1}>
                              {selectedNotification.target_provinces_display.map(
                                (p) => (
                                  <Badge key={p.id} colorScheme="green">
                                    {p.code}
                                  </Badge>
                                ),
                              )}
                            </HStack>
                          </Box>
                        )}
                      {!selectedNotification.target_provinces_display &&
                        selectedNotification.target_provinces &&
                        selectedNotification.target_provinces.length > 0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.600"
                            >
                              Provinces:
                            </Text>
                            <HStack spacing={2} flexWrap="wrap" mt={1}>
                              {PROVINCES.filter((p) =>
                                selectedNotification.target_provinces.includes(
                                  p.id,
                                ),
                              ).map((p) => (
                                <Badge key={p.id} colorScheme="green">
                                  {p.name}
                                </Badge>
                              ))}
                            </HStack>
                          </Box>
                        )}
                      {selectedNotification.target_roles_display &&
                        selectedNotification.target_roles_display.length >
                          0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.600"
                            >
                              Roles:
                            </Text>
                            <HStack spacing={2} flexWrap="wrap" mt={1}>
                              {selectedNotification.target_roles_display.map(
                                (r) => (
                                  <Badge key={r.id} colorScheme="orange">
                                    {r.name}
                                  </Badge>
                                ),
                              )}
                            </HStack>
                          </Box>
                        )}
                      {!selectedNotification.target_roles_display &&
                        selectedNotification.target_roles &&
                        selectedNotification.target_roles.length > 0 && (
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.600"
                            >
                              Roles:
                            </Text>
                            <HStack spacing={2} flexWrap="wrap" mt={1}>
                              {ROLES.filter((r) =>
                                selectedNotification.target_roles.includes(
                                  r.id,
                                ),
                              ).map((r) => (
                                <Badge key={r.id} colorScheme="orange">
                                  {r.name}
                                </Badge>
                              ))}
                            </HStack>
                          </Box>
                        )}
                      {selectedNotification.target_corporate_leadership && (
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                          >
                            Corporate Leadership:
                          </Text>
                          <Badge colorScheme="purple" mt={1}>
                            Corporate Leadership (Receives ALL Notifications)
                          </Badge>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}

                <Divider />

                <HStack spacing={4} fontSize="sm" color="gray.500">
                  <Text>
                    Created:{' '}
                    {new Date(selectedNotification.created_at).toLocaleString()}
                  </Text>
                </HStack>
              </VStack>
            )}
          </ModalBody>

          <Divider />

          <ModalFooter>
            <HStack w="full" justify="space-between">
              <Button
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  if (selectedNotification) {
                    onViewClose();
                    handleEdit(selectedNotification);
                  }
                }}
              >
                Edit
              </Button>
              <Button onClick={onViewClose}>Close</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        size="lg"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="xl" fontWeight="bold">
              Confirm Communication Send
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <Divider />

          <ModalBody py={6}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Communication Details:
                </Text>
                <VStack align="stretch" spacing={3} pl={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Title:
                    </Text>
                    <Text fontSize="md">{formData.title}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Summary:
                    </Text>
                    <Text fontSize="md">{formData.summary}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Requires Acknowledgement:
                    </Text>
                    <Badge
                      colorScheme={
                        formData.requires_acknowledgement ? 'red' : 'gray'
                      }
                    >
                      {formData.requires_acknowledgement
                        ? 'Yes - Recipients must acknowledge'
                        : 'No'}
                    </Badge>
                  </Box>
                  {formData.scheduled_for && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Scheduled For:
                      </Text>
                      <Text fontSize="md">
                        {formData.scheduled_for.toLocaleString()}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Audience:
                </Text>
                <VStack align="stretch" spacing={2} pl={4}>
                  {formData.target_branches.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Branches/Offices:
                      </Text>
                      <Text fontSize="sm">
                        {branches
                          .filter((b: any) =>
                            formData.target_branches.includes(b.id),
                          )
                          .map((b: any) => b.name)
                          .join(', ')}
                      </Text>
                    </Box>
                  )}
                  {formData.target_provinces.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Provinces:
                      </Text>
                      <Text fontSize="sm">
                        {PROVINCES.filter((p) =>
                          formData.target_provinces.includes(p.id),
                        )
                          .map((p) => p.name)
                          .join(', ')}
                      </Text>
                    </Box>
                  )}
                  {formData.target_roles.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Roles:
                      </Text>
                      <Text fontSize="sm">
                        {ROLES.filter((r) =>
                          formData.target_roles.includes(r.id),
                        )
                          .map((r) => r.name)
                          .join(', ')}
                      </Text>
                    </Box>
                  )}
                  {formData.target_corporate_leadership && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="purple.600"
                      >
                        Corporate Leadership:
                      </Text>
                      <Badge colorScheme="purple">
                        Corporate Leadership (Always Included)
                      </Badge>
                    </Box>
                  )}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="blue.600">
                      Estimated Recipients: {estimatedCount}
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {uploadedFiles.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Attachments:
                    </Text>
                    <VStack align="stretch" spacing={1} pl={4}>
                      {uploadedFiles.map((file, idx) => (
                        <Text key={idx} fontSize="sm">
                          • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                </>
              )}

              <Divider />

              <Box
                p={4}
                bg="yellow.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="yellow.200"
              >
                <Text fontSize="sm" fontWeight="medium" color="yellow.800">
                  ⚠️ Please review all details carefully before sending.
                  {formData.requires_acknowledgement &&
                    ' This is a mandatory notification and will block users from accessing the app until acknowledged.'}
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <Divider />

          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={onConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmSend}
                isLoading={createMutation.isPending}
              >
                {formData.scheduled_for ? 'Schedule Communication' : 'Send Now'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
